import { getTagsApi } from '@/api-calls/tags/get'
import {
  createTagApi,
  deleteTagApi,
  updateTagApi,
} from '@/api-calls/tags/mutate'
import { useAuth } from '@/auth/auth-context'
import { ContainerWithSidebar } from '@/components/header/container'
import { useSite } from '@/hooks/use-site'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Skeleton,
  Textarea,
} from '@blogging/ui'
import { slugify } from '@blogging/utils'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { Loader2, Pencil, Plus, Tag, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface TagItem {
  id: string
  name: string
  slug: string
  description: string | null
  createdById: string
  _count: {
    postsTags: number
  }
}

interface TagFormData {
  name: string
  slug: string
  description: string
}

const defaultFormData: TagFormData = {
  name: '',
  slug: '',
  description: '',
}

export function SiteTagsPage() {
  const { siteId } = useParams()
  const { canManageAllContent } = useSite()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<TagItem | null>(null)
  const [formData, setFormData] = useState<TagFormData>(defaultFormData)

  // Check if user can edit/delete a specific tag
  const canManageTag = (tag: TagItem) => {
    if (canManageAllContent) return true // Admin or Editor can manage all
    return tag.createdById === user?.id // Author can only manage their own
  }

  const {
    data: tags,
    isLoading,
    error,
  } = useFetch(getTagsApi, {
    resourceName: 'tags',
    initialParams: { siteId: siteId as string },
  })

  const { mutate: createTag, isPending: isCreating } = useMutation(
    createTagApi,
    {
      resourceName: 'tag',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getTagsApi.apiName, { siteId }],
        })
        toast.success('Tag created!')
        handleCloseDialog()
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to create tag')
      },
    },
  )

  const { mutate: updateTag, isPending: isUpdating } = useMutation(
    updateTagApi,
    {
      resourceName: 'tag',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getTagsApi.apiName, { siteId }],
        })
        toast.success('Tag updated!')
        handleCloseDialog()
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update tag')
      },
    },
  )

  const { mutate: deleteTag, isPending: isDeleting } = useMutation(
    deleteTagApi,
    {
      resourceName: 'tag',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getTagsApi.apiName, { siteId }],
        })
        toast.success('Tag deleted!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to delete tag')
      },
    },
  )

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingItem(null)
    setFormData(defaultFormData)
  }

  const handleOpenCreate = () => {
    setEditingItem(null)
    setFormData(defaultFormData)
    setIsDialogOpen(true)
  }

  const handleOpenEdit = (item: TagItem) => {
    setEditingItem(item)
    setFormData({
      name: item.name,
      slug: item.slug,
      description: item.description || '',
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name')
      return
    }

    const slug = formData.slug.trim() || slugify(formData.name)

    if (editingItem) {
      updateTag({
        id: editingItem.id,
        siteId,
        name: formData.name,
        slug,
        description: formData.description || null,
      })
    } else {
      createTag({
        siteId,
        name: formData.name,
        slug,
        description: formData.description || null,
      })
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this tag?')) {
      deleteTag({ id, siteId })
    }
  }

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      // Auto-generate slug only when creating new tag
      slug: editingItem ? prev.slug : slugify(name, false),
    }))
  }

  return (
    <ContainerWithSidebar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">Tags</h1>
          <p className="text-muted-foreground">
            Organize your content with tags. Tags help readers discover related
            posts.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">All Tags</h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Tag
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] w-full">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? 'Edit Tag' : 'Create Tag'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingItem
                      ? 'Update the tag details.'
                      : 'Create a new tag to organize your content.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-6 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="Technology"
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                    />
                    <p className="text-[0.8rem] text-muted-foreground">
                      The public name for this tag.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug</Label>
                    <Input
                      id="slug"
                      placeholder="technology"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      onBlur={(e) =>
                        setFormData({
                          ...formData,
                          slug: slugify(e.target.value, false),
                        })
                      }
                    />
                    <p className="text-[0.8rem] text-muted-foreground">
                      The URL-friendly version of the name.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="A brief description of this tag..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="resize-none h-24"
                    />
                    <p className="text-[0.8rem] text-muted-foreground">
                      Optional description shown on the tag page.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                  >
                    {(isCreating || isUpdating) && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    {editingItem ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-14 w-full rounded-lg" />
            </div>
          ) : error ? (
            <div className="text-destructive">Failed to load tags</div>
          ) : tags?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tags created yet.</p>
              <p className="text-sm">Create tags to organize your content.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tags?.map((tag: TagItem) => {
                const canEdit = canManageTag(tag)
                return (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-background"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-muted">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{tag.name}</p>
                        <p className="text-sm text-muted-foreground">
                          /{tag.slug} &middot; {tag._count.postsTags} post
                          {tag._count.postsTags !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(tag)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(tag.id)}
                          disabled={isDeleting}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </ContainerWithSidebar>
  )
}
